import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import useSelector from '@hooks/useSelector'

import { faFile, faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, Grid, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams, sortArrayBy } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useReduxAction from '@hooks/useReduxAction'
import useTemplatePages from '@hooks/useTemplatePages'
import { generateUrls } from '@hooks/useTemplatePage'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { EntitiesState } from '@redux/entities'

import NoFiltersBlankState from '../NoFiltersBlankState'
import TemplateGalleryGridItem from '../TemplateGalleryGridItem'

const buildPageFilterFields = (entities: EntitiesState) => {
  const { services, tags, templatePageCategories } = entities

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Categories',
        inputComponent: 'select',
        style,
      },
      key: 'category_id',
      label: 'Category',
      values: sortArrayBy(Object.values(templatePageCategories), 'asc', 'title').map(category => (
        { key: category.id, label: category.title }
      )),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Tags',
        inputComponent: 'select',
        style,
      },
      key: 'tag_id',
      label: 'Tag',
      values: sortArrayBy(Object.values(tags), 'asc', 'title').map(tag => (
        { key: tag.id, label: tag.title }
      )),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Services',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: sortArrayBy(Object.values(services), 'asc', 'title').map(service => (
        { key: service.id, label: service.title }
      )),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      key: 'service_product_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useReduxAction('services', 'loadServices')
  useReduxAction('templatePageCategories', 'loadTemplatePageCategories')
  useReduxAction('tags', 'loadTags', { usable_tags_only: true })

  const entities = useSelector(reduxState => reduxState.entities)

  useLocation()

  const {
    hidden = 'false',
  } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden']) as { hidden: 'true' | 'false' }

  const { selectedAgency, selectedAgencyId } = useAgencySelector()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const hasSelectedFilters = !!Object.keys(pageFilters).length

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...pageFilters,
      active: 'true',
      agency_id: hidden === 'false' ? selectedAgencyId : undefined,
      brand_theme_id: selectedAgency.brand_theme_id,
      not_assigned_to_agency_id: hidden === 'true' ? selectedAgencyId : undefined,
    },
    performHttpRequests: hasSelectedFilters,
    requestOptions: {
      agency_providers_only: true,
      all_brand_templates: true,
      include_template_page_is_active: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  const { templatePagesIndexUrl, unassignedTemplatePagesUrl } = generateUrls({}, {
    prefixRoute: `agencies/${selectedAgencyId}/manage`,
  })

  return (
    <>
      <Helmet>
        <title>Template Gallery | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={hidden}
        nestedNavigation
        tabBarItems={[
          {
            href: templatePagesIndexUrl,
            icon: faHome,
            key: 'false',
            title: 'Available Templates',
          },
          {
            href: unassignedTemplatePagesUrl,
            icon: faFile,
            key: 'true',
            title: 'Hidden Templates',
          },
        ]}
        title="Template Gallery"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.templateGallery.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasSelectedFilters && (
              <NoFiltersBlankState />
            )}

            {!loading && hasSelectedFilters && !hasFilteredTemplatePages && <ListBlankState />}

            {!loading && hasSelectedFilters && hasFilteredTemplatePages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Templates">
                <Grid
                  // @ts-expect-error TS2322
                  gridGap="medium"
                  gridAutoRows="1fr"
                  gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
                  padding="large"
                >
                  {filteredTemplatePages.map(templatePage => (
                    <TemplateGalleryGridItem key={templatePage.id} templatePage={templatePage} />
                  ))}
                </Grid>

                <DashboardModule.LoadMoreFooter
                  boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.templateGallery.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(entities)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
